import { Slider } from '@mui/material';
import { vec3 } from 'gl-matrix';
import React, { useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { Orientations } from '~/engine/view/views/PlaneView';
import { useSlider } from '~/hooks/useSlider';
import { selectedROIState } from '~/state/atoms/structureSet/roiAtoms';
import {
    currenPositionAutoScrollState,
    isAutoScrollingState,
} from '~/state/atoms/viewerAtoms';

export const MovieReviewSlider: React.FC = () => {
    const selectedROI = useRecoilValue(selectedROIState);
    const {
        sliderValue,
        handleChange,
        minPosition,
        maxPosition,
        currentOrientation,
    } = useSlider(selectedROI);
    const currentPosition = useRecoilValue(currenPositionAutoScrollState);
    const [shouldScroll, setShouldScroll] =
        useRecoilState(isAutoScrollingState);
    const [isUserInteracting, setIsUserInteracting] = useState(false);

    // Effect to update the slider position when the movie is running (auto-scrolling)
    useEffect(() => {
        if (!isUserInteracting && shouldScroll && currentPosition) {
            handleChange({}, getAutoScrollValue(currentPosition)); // Dynamically determine the scroll value
        }
    }, [currentPosition, shouldScroll, isUserInteracting, handleChange]);

    const handleSliderChange = (
        _event: unknown,
        newValue: number | number[]
    ) => {
        // Stop auto-scrolling if the user interacts with the slider
        setIsUserInteracting(true);
        setShouldScroll(false);
        handleChange(_event, newValue);
    };

    const handleSliderChangeCommitted = () => {
        // Once the user is done interacting, allow the slider to update again
        setIsUserInteracting(false);
    };

    // Function to get the appropriate auto-scroll value based on the current orientation
    const getAutoScrollValue = (currentPosition: vec3) => {
        switch (currentOrientation) {
            case Orientations.SAGITTAL_ORIENTATION:
                return currentPosition[0]; // Use x-axis for sagittal orientation
            case Orientations.CORONAL_ORIENTATION:
                return currentPosition[1]; // Use y-axis for coronal orientation
            case Orientations.TRANSVERSAL_ORIENTATION:
                return currentPosition[2]; // Use z-axis for transversal orientation
            default:
                return currentPosition[2]; // Default to z-axis
        }
    };

    // Get the current slider value based on whether it's auto-scrolling or user-controlled
    const getSliderValue = () => {
        if (shouldScroll && currentPosition) {
            return getAutoScrollValue(currentPosition);
        }
        return sliderValue;
    };

    return (
        <Slider
            value={getSliderValue()}
            min={minPosition}
            max={maxPosition}
            step={1}
            onChange={handleSliderChange}
            onChangeCommitted={handleSliderChangeCommitted}
            aria-labelledby="movie-review-slider"
        />
    );
};
